/**
 * Work Log 관련 유틸리티 함수
 */

import { WorkLogStatus, WorkLogCategory, STATUS_COLORS, CATEGORY_COLORS, DATETIME_INDEX } from '../constants/workLog'

/**
 * Spring Boot LocalDateTime 배열 또는 ISO 문자열을 Date 객체로 변환
 */
export function parseDateTime(dateTime: string | number[] | null): Date | null {
  if (!dateTime) return null

  if (Array.isArray(dateTime)) {
    // Spring Boot returns LocalDateTime as array: [year, month, day, hour, minute, second, nanosecond]
    const year = dateTime[DATETIME_INDEX.YEAR]
    const month = dateTime[DATETIME_INDEX.MONTH] - 1 // JavaScript month is 0-indexed
    const day = dateTime[DATETIME_INDEX.DAY]
    const hour = dateTime[DATETIME_INDEX.HOUR] || 0
    const minute = dateTime[DATETIME_INDEX.MINUTE] || 0
    const second = dateTime[DATETIME_INDEX.SECOND] || 0

    return new Date(year, month, day, hour, minute, second)
  }

  return new Date(dateTime)
}

/**
 * 날짜/시간을 한국 형식으로 포맷팅
 */
export function formatDateTime(dateTime: string | number[] | null): string {
  if (!dateTime) return 'N/A'

  const date = parseDateTime(dateTime)
  if (!date) return 'N/A'

  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * 초를 "X시간 Y분" 형식으로 변환
 */
export function formatDuration(seconds: number | null): string {
  if (!seconds) return 'N/A'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  return `${hours}시간 ${minutes}분`
}

/**
 * Work Log 상태에 따른 Tailwind CSS 클래스 반환
 */
export function getStatusColor(status: WorkLogStatus): string {
  return STATUS_COLORS[status]
}

/**
 * Work Log 카테고리에 따른 Tailwind CSS 클래스 반환
 */
export function getCategoryColor(category: WorkLogCategory): string {
  return CATEGORY_COLORS[category] || 'bg-gray-100 text-gray-800'
}

/**
 * Work Log 배열을 정렬 (IN_PROGRESS 우선, 최신순)
 */
export function sortWorkLogs<T extends { status: WorkLogStatus; createdAt: string | number[] }>(workLogs: T[]): T[] {
  return [...workLogs].sort((a, b) => {
    // First priority: IN_PROGRESS before COMPLETED
    if (a.status !== b.status) {
      return a.status === 'IN_PROGRESS' ? -1 : 1
    }

    // Second priority: most recent first (by createdAt DESC)
    const getTimestamp = (dateTime: string | number[]) => {
      const date = parseDateTime(dateTime)
      return date ? date.getTime() : 0
    }

    return getTimestamp(b.createdAt) - getTimestamp(a.createdAt)
  })
}
